/**
 * 跨平台 skill 装载逻辑（可编译版，供 postinstall 与 `baskreport install` 共用）。
 *
 * 与 scripts/install-skill.ts 等价，但不依赖 tsx / devDependencies，由 tsup 编译进 dist/。
 *
 * 客户端挂载策略：
 *   - codebuddy / workbuddy：用户级目录链接（junction / symlink）装到 ~/.{client}/skills/<name>
 *   - claude / cursor：项目级文件（.claude/commands、.cursor/rules），需在使用者自己的项目里生成，
 *     故 postinstall 默认只挂 codebuddy/workbuddy；项目级由使用者手动 `baskreport install` 触发。
 *
 * 用法:
 *   node dist/install.js [--clients codebuddy,workbuddy] [--project-root <dir>] [--build]
 *   baskreport install [--clients claude,cursor] [--project-root <dir>]
 */
import { execSync } from "node:child_process";
import * as fs from "node:fs";
import * as path from "node:path";
import * as os from "node:os";
import { fileURLToPath } from "node:url";

interface Options {
  build: boolean;
  clients: Client[];
  projectRoot?: string;
}

export const KNOWN_CLIENTS = ["codebuddy", "workbuddy", "codearts", "claude", "cursor"] as const;
export type Client = (typeof KNOWN_CLIENTS)[number];

export function expandClients(raw: string[]): Client[] {
  const set = new Set<Client>();
  for (const c of raw) {
    if (c === "all") {
      KNOWN_CLIENTS.forEach((k) => set.add(k));
    } else if ((KNOWN_CLIENTS as readonly string[]).includes(c)) {
      set.add(c as Client);
    } else {
      throw new Error(`未知客户端: ${c}。支持: ${KNOWN_CLIENTS.join(", ")} 或 all`);
    }
  }
  return [...set];
}

export function parseArgs(argv: string[]): Options {
  const options: Options = { build: false, clients: ["codebuddy", "workbuddy"] };
  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (!arg) continue;
    if (arg === "--build") {
      options.build = true;
    } else if (arg === "--clients") {
      const val = argv[++i];
      if (!val) throw new Error("--clients 需要一个逗号分隔的客户端列表");
      options.clients = expandClients(val.split(",").map((c) => c.trim()).filter(Boolean));
    } else if (arg.startsWith("--clients=")) {
      options.clients = expandClients(
        arg.slice("--clients=".length).split(",").map((c) => c.trim()).filter(Boolean),
      );
    } else if (arg === "--project-root") {
      const val = argv[++i];
      if (!val) throw new Error("--project-root 需要一个目录路径");
      options.projectRoot = val;
    } else if (arg.startsWith("--project-root=")) {
      options.projectRoot = arg.slice("--project-root=".length);
    } else {
      throw new Error(`未知参数: ${arg}`);
    }
  }
  return options;
}

function isLink(targetPath: string): boolean {
  try {
    return fs.lstatSync(targetPath).isSymbolicLink();
  } catch {
    return false;
  }
}

function readSkillMeta(skillMd: string): { description: string; body: string } {
  const raw = fs.readFileSync(skillMd, "utf8");
  const fm = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n?/);
  let description = "";
  let body = raw;
  if (fm) {
    const fmText = fm[1] ?? "";
    const m = fmText.match(/^\s*description:\s*([\s\S]*?)(?:\n\w|$)/m);
    if (m && m[1]) description = m[1].trim();
    body = raw.slice(fm[0].length);
  }
  return { description, body: body.replace(/^\n+/, "") };
}

function genClaudeCommand(projectRoot: string, body: string): void {
  const dir = path.join(projectRoot, ".claude", "commands");
  fs.mkdirSync(dir, { recursive: true });
  const out = path.join(dir, "baskreport.md");
  const header = [
    "<!--",
    "此文件由 baskreport install 自动生成；如需修改请改 SKILL.md 后重跑。",
    "在 Claude Code 中用 /baskreport 调用。",
    "-->",
    "",
  ].join("\n");
  fs.writeFileSync(out, header + body, "utf8");
  console.log(`  已生成: ${out}`);
}

function genCursorRule(projectRoot: string, description: string, body: string): void {
  const dir = path.join(projectRoot, ".cursor", "rules");
  fs.mkdirSync(dir, { recursive: true });
  const out = path.join(dir, "basksoft-ai.mdc");
  const fm = [
    "---",
    `description: ${description}`,
    "alwaysApply: false",
    "globs:",
    "  - '**/*.json'",
    "  - '**/*.xml'",
    "---",
    "",
  ].join("\n");
  const header = [
    "<!--",
    "此文件由 baskreport install 自动生成；如需修改请改 SKILL.md 后重跑。",
    "-->",
    "",
  ].join("\n");
  fs.writeFileSync(out, fm + header + body, "utf8");
  console.log(`  已生成: ${out}`);
}

export function runInstall(opts: Options, skillRoot: string): void {
  const skillMd = path.join(skillRoot, "SKILL.md");
  if (!fs.existsSync(skillMd)) {
    throw new Error(`未找到 SKILL.md，请确认运行目录正确。skillRoot=${skillRoot}`);
  }

  const skillName = path.basename(skillRoot);
  console.log(`Skill 名称: ${skillName}`);
  console.log(`仓库路径:   ${skillRoot}`);
  console.log(`目标客户端: ${opts.clients.join(", ")}`);

  if (opts.build) {
    console.log("\n[1/3] 构建产物...");
    execSync("npm install && npm run build", { cwd: skillRoot, stdio: "inherit" });
  } else {
    console.log("\n[1/3] 跳过构建");
  }

  const { description, body } = readSkillMeta(skillMd);

  console.log("[2/3] 创建客户端链接 / 生成命令与规则...");
  const homeDir = os.homedir();

  for (const client of opts.clients) {
    if (client === "claude") {
      const root = opts.projectRoot ?? skillRoot;
      genClaudeCommand(root, body);
      continue;
    }
    if (client === "cursor") {
      const root = opts.projectRoot ?? skillRoot;
      genCursorRule(root, description, body);
      continue;
    }

    const skillsDir =
      client === "codearts"
        ? path.join(homeDir, ".codeartsdoer", "skills")
        : path.join(homeDir, `.${client}`, "skills");
    const linkPath = path.join(skillsDir, skillName);
    fs.mkdirSync(skillsDir, { recursive: true });

    if (fs.existsSync(linkPath)) {
      if (isLink(linkPath)) {
        console.log(`  移除旧链接: ${linkPath}`);
        fs.rmSync(linkPath, { force: true, recursive: true });
      } else {
        throw new Error(
          `目标已存在且为真实目录，已中止以免误删数据: ${linkPath}\n请手动处理后再运行。`,
        );
      }
    }

    const type = process.platform === "win32" ? "junction" : "dir";
    fs.symlinkSync(skillRoot, linkPath, type);
    console.log(`  已创建: ${linkPath} -> ${skillRoot}`);
  }

  console.log("\n[3/3] 完成。");
  console.log(`  - codebuddy / workbuddy / codearts：在对应客户端重新加载即可使用 skill '${skillName}'。`);
  console.log(`  - claude：用 /baskreport 触发（命令文件位于 .claude/commands/baskreport.md）。`);
  console.log(`  - cursor：打开项目后提及 BaskReport 报表需求会自动加载 .cursor/rules/basksoft-ai.mdc。`);
}

/**
 * 卸载 skill：对称地移除 install 创建的内容。
 *
 *   - codebuddy / workbuddy：仅删除 ~/.{client}/skills/<name> 这个 junction / symlink，
 *     绝不删除真实仓库目录（依赖 isLink 安全判定）。
 *   - claude：删除项目级 .claude/commands/baskreport.md（提示：该文件可能被 git 跟踪）。
 *   - cursor：删除项目级 .cursor/rules/basksoft-ai.mdc（提示：该文件可能被 git 跟踪）。
 */
export function runUninstall(opts: Options, skillRoot: string): void {
  const skillName = path.basename(skillRoot);
  console.log(`Skill 名称: ${skillName}`);
  console.log(`仓库路径:   ${skillRoot}`);
  console.log(`目标客户端: ${opts.clients.join(", ")}`);

  const homeDir = os.homedir();

  console.log("\n[1/2] 移除客户端链接 / 命令与规则...");
  for (const client of opts.clients) {
    if (client === "claude") {
      const root = opts.projectRoot ?? skillRoot;
      const out = path.join(root, ".claude", "commands", "baskreport.md");
      removeFile(out, "claude 命令文件");
      continue;
    }
    if (client === "cursor") {
      const root = opts.projectRoot ?? skillRoot;
      const out = path.join(root, ".cursor", "rules", "basksoft-ai.mdc");
      removeFile(out, "cursor 规则文件");
      continue;
    }

    const skillsDir =
      client === "codearts"
        ? path.join(homeDir, ".codeartsdoer", "skills")
        : path.join(homeDir, `.${client}`, "skills");
    const linkPath = path.join(skillsDir, skillName);

    if (!fs.existsSync(linkPath)) {
      console.log(`  不存在（跳过）: ${linkPath}`);
      continue;
    }

    if (isLink(linkPath)) {
      console.log(`  移除链接: ${linkPath}`);
      fs.rmSync(linkPath, { force: true, recursive: true });
    } else {
      throw new Error(
        `目标已存在且为真实目录，已中止以免误删数据: ${linkPath}\n请手动处理后再运行。`,
      );
    }
  }

  console.log("\n[2/2] 完成。对应客户端重新加载后将不再识别该 skill。");
}

function removeFile(target: string, label: string): void {
  if (!fs.existsSync(target)) {
    console.log(`  ${label}不存在（跳过）: ${target}`);
    return;
  }
  fs.rmSync(target, { force: true });
  console.log(`  已删除: ${target}`);
  console.log(
    `    （提示：${label}可能为 git 跟踪文件，必要时请手动 \`git rm\` 并提交。）`,
  );
}

// 作为 postinstall 脚本（node dist/install.js）直接运行时：
//   - 不依赖项目仓库，仅挂用户级 codebuddy/workbuddy
//   - skillRoot 指向包自身目录（dist 的上一级 = 包根）
// 作为 postinstall 脚本（node dist/install.js）直接运行时：
//   - 不依赖项目仓库，仅挂用户级 codebuddy/workbuddy
//   - skillRoot 指向包自身目录（dist 的上一级 = 包根）
// 判断依据：本模块文件本身是否就是 node 的入口脚本（文件名 = install.js）。
const currentFile = fileURLToPath(import.meta.url);
const invokedFile = process.argv[1] ? path.resolve(process.argv[1]) : "";
if (invokedFile && path.basename(currentFile) === "install.js" && currentFile === invokedFile) {
  const skillRoot = path.resolve(path.dirname(currentFile), "..");
  const opts = parseArgs(process.argv.slice(2));
  runInstall(opts, skillRoot);
}
